"use client";
import { productEvent, resultEvent } from "@/lib/atInternetTags";
import { canUseSearchInsights } from "@/lib/searchInsightsConsent";
import clsx from "clsx";
import type { SearchDeparture, SearchTravel } from "front-search-schema/algolia";
import { type ReactNode, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import searchInsights from "search-insights";
import { termStore } from "../../_utils/searchStore";

type Props = {
  children: ReactNode;
  travel: SearchTravel;
  queryId?: string;
  position?: number;
  departure: SearchDeparture | undefined;
  className?: string;
  page?: number;
};
export const Container = ({ children, travel, departure, queryId, position, className, page = 1 }: Props) => {
  const { ref, inView } = useInView({
    rootMargin: "500px",
    triggerOnce: true,
  });

  const trackSearchResultClicked = () => {
    if (queryId && position && canUseSearchInsights()) {
      searchInsights("clickedObjectIDsAfterSearch", {
        index: "search_travels",
        eventName: "SearchResultClicked",
        queryID: queryId,
        objectIDs: [travel.id.toString()],
        positions: [position],
      });
    }

    if (departure) {
      const term = termStore.get() || "";

      resultEvent({
        ise_keyword: term,
        ise_click_rank: position || 0,
        ise_page: page,
      });
    }
  };

  useEffect(() => {
    if (!departure) return;

    if (inView) {
      if (canUseSearchInsights()) {
        searchInsights("viewedObjectIDs", {
          index: "search_travels",
          eventName: "SearchResultViewed",
          objectIDs: [travel.id.toString()],
        });
      }

      productEvent({
        event: "product.display",
        product_id: travel.id.toString(),
        product: travel.alias || undefined,
        product_variant: travel.travelTypeId,
        product_brand: travel.brandTitle || undefined,
        product_discount: departure.primoPrice !== undefined,
        product_pricetaxincluded: departure.primoPrice
          ? departure.defaultPrice - departure.primoPrice
          : departure.defaultPrice,
        product_currency: "EUR",
        speaker_ids: departure.speakers.map((speaker) => speaker.id.toString()),
        registration_status: departure.state.registrationStatus,
        travel_duration: departure.days || undefined,
      });
    }
  }, [inView, travel, departure]);

  return (
    <div className="@container" onClick={trackSearchResultClicked} ref={ref}>
      <div
        className={clsx(
          "bg-white border border-bg-2 rounded-lg relative shadow-c1 overflow-hidden",
          {
            "grayscale opacity-90": !departure,
          },
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
};
